import type { ReactNode } from 'react'
import type { CelestialBody } from '../lore'
import { Markdown } from './Markdown'
import { SpeciesList } from './SpeciesList'
import { LoreSection } from './LorebookShell'
import type { Chapter } from './LorebookShell'

type Variant = 'compact' | 'reader'

/** The section ids used for anchor jump-links in the reader. Keep in sync with bodyChapters(). */
export const BODY_SECTION_IDS = {
  anomalies: 'anomalies',
  moons: 'moons',
  orbit: 'orbit',
  surface: 'surface',
  vegetation: 'vegetation',
  plants: 'plants',
  ecosystems: 'ecosystems',
  wildlife: 'wildlife',
  fauna: 'fauna',
} as const

/** Build the in-page chapter list for a body (used by the reader's right TOC). */
export function bodyChapters(body: CelestialBody): Chapter[] {
  const c: Chapter[] = []
  if (body.anomalies?.length) c.push({ id: BODY_SECTION_IDS.anomalies, label: 'Anomalies' })
  if (body.moons?.length) c.push({ id: BODY_SECTION_IDS.moons, label: 'Moons' })
  if (body.fleets?.length) c.push({ id: BODY_SECTION_IDS.orbit, label: 'In Orbit' })
  if (body.layoutNotes) c.push({ id: BODY_SECTION_IDS.surface, label: 'Surface' })
  if (body.vegetationRules) c.push({ id: BODY_SECTION_IDS.vegetation, label: 'Vegetation' })
  if (body.plants?.length) c.push({ id: BODY_SECTION_IDS.plants, label: 'Key Plants' })
  if (body.ecosystems?.length) c.push({ id: BODY_SECTION_IDS.ecosystems, label: 'Ecosystems' })
  if (body.wildlifeNotes) c.push({ id: BODY_SECTION_IDS.wildlife, label: 'Wildlife' })
  if (body.species?.length) c.push({ id: BODY_SECTION_IDS.fauna, label: 'Fauna' })
  for (const s of body.sections ?? []) c.push({ id: s.id, label: s.title })
  return c
}

/** A titled section that adapts to its context: compact inline (map panel) or
 *  a styled, anchored reader section (lorebook detail page). */
function Section({
  variant,
  id,
  title,
  children,
}: {
  variant: Variant
  id: string
  title: string
  children: ReactNode
}) {
  if (variant === 'reader') {
    return (
      <LoreSection id={id} title={title}>
        {children}
      </LoreSection>
    )
  }
  return (
    <div className="mt-6">
      <div className="mb-2 text-[11px] uppercase tracking-[0.3em] text-slate-500">{title}</div>
      {children}
    </div>
  )
}

/**
 * Every optional detail section for a celestial body: anomalies, moons, fleets,
 * surface layout, vegetation rules, key plants, ecosystems, wildlife notes, and
 * fauna. Shared by the map slide-in panel (compact) and the lorebook detail page
 * (reader, with anchored sections + TOC).
 */
export function BodyExtras({ body, variant = 'compact' }: { body: CelestialBody; variant?: Variant }) {
  return (
    <>
      {body.anomalies && body.anomalies.length > 0 && (
        <Section variant={variant} id={BODY_SECTION_IDS.anomalies} title="Anomalies">
          <div className="divide-y divide-edge">
            {body.anomalies.map((a, i) => (
              <div key={i} className="md py-4 text-sm first:pt-0 last:pb-0">
                <Markdown>{a}</Markdown>
              </div>
            ))}
          </div>
        </Section>
      )}

      {body.moons && body.moons.length > 0 && (
        <Section variant={variant} id={BODY_SECTION_IDS.moons} title="Moons">
          <div className="divide-y divide-edge">
            {body.moons.map((m) => (
              <div key={m.id} id={`moon-${m.id}`} className="py-4 first:pt-0 last:pb-0 scroll-mt-24">
                <div className="font-display text-white">{m.name}</div>
                {m.tagline && <div className="text-xs text-slate-400">{m.tagline}</div>}
                <div className="md mt-2 text-sm">
                  <Markdown>{m.lore}</Markdown>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {body.layoutNotes && (
        <Section variant={variant} id={BODY_SECTION_IDS.surface} title="Surface">
          <div className="md text-sm">
            <Markdown>{body.layoutNotes}</Markdown>
          </div>
        </Section>
      )}

      {body.vegetationRules && (
        <Section variant={variant} id={BODY_SECTION_IDS.vegetation} title="Vegetation">
          <div className="md text-sm">
            <Markdown>{body.vegetationRules}</Markdown>
          </div>
        </Section>
      )}

      {body.plants && body.plants.length > 0 && (
        <Section variant={variant} id={BODY_SECTION_IDS.plants} title="Key Plants">
          <div className="divide-y divide-edge">
            {body.plants.map((p) => (
              <div key={p.id} id={`plant-${p.id}`} className="py-4 first:pt-0 last:pb-0 scroll-mt-24">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-display text-white">{p.name}</span>
                  {p.region && <span className="text-xs text-slate-500">· {p.region}</span>}
                </div>
                {p.tagline && <div className="mt-0.5 text-xs text-slate-400">{p.tagline}</div>}
                <div className="md mt-2 text-sm">
                  <Markdown>{p.lore}</Markdown>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {body.ecosystems && body.ecosystems.length > 0 && (
        <Section variant={variant} id={BODY_SECTION_IDS.ecosystems} title="Ecosystems">
          <div className="divide-y divide-edge">
            {body.ecosystems.map((e) => (
              <div key={e.id} className="py-4 first:pt-0 last:pb-0">
                <div className="font-display text-white">{e.name}</div>
                {e.region && <div className="text-xs text-slate-500">{e.region}</div>}
                {e.tagline && <div className="mt-1 text-xs text-slate-400">{e.tagline}</div>}
                <div className="md mt-2 text-sm">
                  <Markdown>{e.lore}</Markdown>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {body.wildlifeNotes && (
        <Section variant={variant} id={BODY_SECTION_IDS.wildlife} title="Wildlife in General">
          <div className="md text-sm">
            <Markdown>{body.wildlifeNotes}</Markdown>
          </div>
        </Section>
      )}

      {body.species && body.species.length > 0 && (
        <Section variant={variant} id={BODY_SECTION_IDS.fauna} title="Fauna">
          <SpeciesList species={body.species} />
        </Section>
      )}

      {body.sections?.map((s) => (
        <Section key={s.id} variant={variant} id={s.id} title={s.title}>
          <div className="md text-sm">
            <Markdown>{s.body}</Markdown>
          </div>
        </Section>
      ))}
    </>
  )
}
